<script setup>
import { computed, getCurrentInstance, watch } from 'vue';
import { usePage } from '@inertiajs/vue3';
import BaseDashboardBreadcrumb from '@/layout/base/BaseDashboardBreadcrumb.vue';
import { pageTitles } from '@/layout/admin/menu.js';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const page = usePage();

// Get current route name and generate breadcrumb items specific to admin
const breadcrumbItems = computed(() => {
    const currentUrl = page.url;
    const routeName = route().current();
    const breadcrumbs = [];
    
    const previousRoute = sessionStorage.getItem('previousRoute');
    const cameFromDashboard = previousRoute === 'dashboard';
    
    const addContextualBreadcrumb = (pageKey, parentPageKey = null) => {
        if (cameFromDashboard && parentPageKey && routeName?.endsWith('.index')) {
            breadcrumbs.push($t(pageTitles('dashboard')));
            breadcrumbs.push($t(pageTitles(parentPageKey)));
            return;
        }
        const specialCases = {
            'orders.index': { 
                condition: routeName?.startsWith('orders') && routeName !== 'orders.index',
                breadcrumb: ['orderManagement', 'allOrders', pageKey]
            },
            'content.banners.index': { 
                condition: routeName?.startsWith('content.banners') && routeName !== 'content.banners.index',
                breadcrumb: ['contentManagement', 'banners', pageKey]
            },
            'content.news.index': { 
                condition: routeName?.startsWith('content.news') && routeName !== 'content.news.index',
                breadcrumb: ['contentManagement', 'newsPosts', pageKey]
            }
        };
        for (const [route, config] of Object.entries(specialCases)) {
            if (previousRoute === route && config.condition) {
                config.breadcrumb.forEach(key => breadcrumbs.push($t(pageTitles(key))));
                return;
            }
        }
        if (parentPageKey) {
            breadcrumbs.push($t(pageTitles(parentPageKey)));
            breadcrumbs.push($t(pageTitles(pageKey)));
        } else {
            breadcrumbs.push($t(pageTitles(pageKey)));
        }
    };

    if (routeName === 'dashboard') {
        breadcrumbs.push($t(pageTitles('dashboard')));
        sessionStorage.setItem('previousRoute', 'dashboard');
    } else if (routeName?.startsWith('sites')) {
        if (routeName === 'sites.index') {
            addContextualBreadcrumb('allSites', 'siteManagement');
        } else if (routeName === 'sites.create') {
            addContextualBreadcrumb('createSite', 'siteManagement');
        } else if (routeName === 'sites.edit') {
            addContextualBreadcrumb('editSite', 'siteManagement');
        } else if (routeName === 'sites.show') {
            addContextualBreadcrumb('siteDetails', 'siteManagement');
        } else if (routeName === 'sites.import.show') {
            addContextualBreadcrumb('importSites', 'siteManagement');
        }
        sessionStorage.setItem('previousRoute', routeName);
    } else if (routeName?.startsWith('site-blacklist')) {
        addContextualBreadcrumb('blacklist', 'siteManagement');
        sessionStorage.setItem('previousRoute', routeName);
    } else if (routeName?.startsWith('budgets')) {
        if (routeName === 'budgets.index') {
            addContextualBreadcrumb('allBudgets', 'orderManagement');
        } else if (routeName === 'budgets.create') {
            const isEditing = currentUrl.includes('editBudgetNumber');
            addContextualBreadcrumb(isEditing ? 'editBudget' : 'createBudget', 'orderManagement');
        } else if (routeName === 'budgets.edit') {
            addContextualBreadcrumb('editBudget', 'orderManagement');
        }
        sessionStorage.setItem('previousRoute', routeName);
    } else if (routeName?.startsWith('orders') || currentUrl.includes('/orders/')) {
        if (routeName === 'orders.index') {
            addContextualBreadcrumb('allOrders', 'orderManagement');
        } else if (routeName === 'orders.create') {
            addContextualBreadcrumb('createOrder', 'orderManagement');
        } else if (routeName === 'orders.edit') {
            addContextualBreadcrumb('editOrder', 'orderManagement');
        } else if (routeName === 'orders.show') {
            addContextualBreadcrumb('orderDetails', 'orderManagement');
        } else if (routeName === 'orders.administration' || currentUrl.includes('/order-administration')) {
            addContextualBreadcrumb('orderAdministration', 'orderManagement');
        } else if (routeName === 'orders.content-review' || currentUrl.includes('/content-review')) {
            addContextualBreadcrumb('contentReview', 'orderManagement');
        } else if (routeName === 'orders.publication' || currentUrl.includes('/publication')) {
            addContextualBreadcrumb('orderPublication', 'orderManagement');
        } else if (routeName === 'orders.live-link' || currentUrl.includes('/live-link')) {
            addContextualBreadcrumb('liveLink', 'orderManagement');
        } else if (routeName === 'orders.invoice' || currentUrl.includes('/orders/') && currentUrl.includes('/invoice')) {
            addContextualBreadcrumb('orderInvoice', 'orderManagement');
        }
        sessionStorage.setItem('previousRoute', routeName);
    } else if (routeName?.startsWith('clients')) {
        if (routeName === 'clients.index') {
            addContextualBreadcrumb('clients', 'userManagement');
        } else if (routeName === 'clients.create') {
            addContextualBreadcrumb('addClient', 'userManagement');
        } else if (routeName === 'clients.edit') {
            addContextualBreadcrumb('editClient', 'userManagement');
        } else if (routeName === 'clients.show') {
            addContextualBreadcrumb('clientDetails', 'userManagement');
        }
        sessionStorage.setItem('previousRoute', routeName);
    } else if (routeName?.startsWith('sellers')) {
        if (routeName === 'sellers.index') {
            addContextualBreadcrumb('allSellers', 'sellerManagement');
        } else if (routeName === 'sellers.create') {
            addContextualBreadcrumb('addSeller', 'sellerManagement');
        } else if (routeName === 'sellers.edit') {
            addContextualBreadcrumb('editSeller', 'sellerManagement');
        } else if (routeName === 'sellers.show') {
            addContextualBreadcrumb('sellerDetails', 'sellerManagement');
        }
        sessionStorage.setItem('previousRoute', routeName);
    } else if (routeName?.startsWith('categories')) {
        if (routeName === 'categories.index') {
            addContextualBreadcrumb('allCategories', 'categoriesManagement');
        } else if (routeName === 'categories.create') {
            addContextualBreadcrumb('addCategory', 'categoriesManagement');
        } else if (routeName === 'categories.edit') {
            addContextualBreadcrumb('editCategory', 'categoriesManagement');
        } else if (routeName === 'categories.show') {
            addContextualBreadcrumb('categoryDetails', 'categoriesManagement');
        }
        sessionStorage.setItem('previousRoute', routeName);
    } else if (routeName?.startsWith('types')) {
        if (routeName === 'types.index') {
            addContextualBreadcrumb('allTypes', 'typesManagement');
        } else if (routeName === 'types.create') {
            addContextualBreadcrumb('addType', 'typesManagement');
        } else if (routeName === 'types.edit') {
            addContextualBreadcrumb('editType', 'typesManagement');
        } else if (routeName === 'types.show') {
            addContextualBreadcrumb('typeDetails', 'typesManagement');
        }
        sessionStorage.setItem('previousRoute', routeName);
    } else if (routeName?.startsWith('languages')) {
        if (routeName === 'languages.index') {
            addContextualBreadcrumb('allLanguages', 'languagesManagement');
        } else if (routeName === 'languages.create') {
            addContextualBreadcrumb('addLanguage', 'languagesManagement');
        } else if (routeName === 'languages.edit') {
            addContextualBreadcrumb('editLanguage', 'languagesManagement');
        } else if (routeName === 'languages.show') {
            addContextualBreadcrumb('languageDetails', 'languagesManagement');
        }
        sessionStorage.setItem('previousRoute', routeName);
    } else if (routeName?.startsWith('countries')) {
        if (routeName === 'countries.index') {
            addContextualBreadcrumb('allCountries', 'countriesManagement');
        } else if (routeName === 'countries.create') {
            addContextualBreadcrumb('addCountry', 'countriesManagement');
        } else if (routeName === 'countries.edit') {
            addContextualBreadcrumb('editCountry', 'countriesManagement');
        } else if (routeName === 'countries.show') {
            addContextualBreadcrumb('countryDetails', 'countriesManagement');
        }
        sessionStorage.setItem('previousRoute', routeName);
    } else if (routeName?.startsWith('teams')) {
        if (routeName === 'teams.index') {
            addContextualBreadcrumb('allTeams', 'teamsManagement');
        } else if (routeName === 'teams.create') {
            addContextualBreadcrumb('addTeam', 'teamsManagement');
        } else if (routeName === 'teams.edit') {
            addContextualBreadcrumb('editTeam', 'teamsManagement');
        } else if (routeName === 'teams.show') {
            addContextualBreadcrumb('teamDetails', 'teamsManagement');
        }
        sessionStorage.setItem('previousRoute', routeName);
    } else if (routeName?.startsWith('exchanges')) {
        if (routeName === 'exchanges.index') {
            addContextualBreadcrumb('allExchangeRates', 'exchangeRates');
        } else if (routeName === 'exchanges.create') {
            addContextualBreadcrumb('addExchangeRate', 'exchangeRates');
        } else if (routeName === 'exchanges.edit') {
            addContextualBreadcrumb('editExchangeRate', 'exchangeRates');
        } else if (routeName === 'exchanges.show') {
            addContextualBreadcrumb('exchangeDetails', 'exchangeRates');
        }
        sessionStorage.setItem('previousRoute', routeName);
    } else if (routeName?.startsWith('invoices')) {
        if (routeName === 'invoices.index') {
            addContextualBreadcrumb('allInvoices', 'invoiceManagement');
        } else if (routeName === 'invoices.edit') {
            addContextualBreadcrumb('editInvoice', 'invoiceManagement');
        }
        sessionStorage.setItem('previousRoute', routeName);
    } else if (routeName?.startsWith('content')) {
        if (routeName === 'content.banners.index') {
            addContextualBreadcrumb('banners', 'contentManagement');
        } else if (routeName === 'content.banners.create' || currentUrl.includes('/content/banners/create')) {
            addContextualBreadcrumb('createBanner', 'contentManagement');
        } else if (routeName === 'content.banners.edit' || (currentUrl.includes('/content/banners/') && currentUrl.includes('/edit'))) {
            addContextualBreadcrumb('editBanner', 'contentManagement');
        } else if (routeName === 'content.news.index') {
            addContextualBreadcrumb('newsPosts', 'contentManagement');
        } else if (routeName === 'content.news.create' || currentUrl.includes('/content/news/create')) {
            addContextualBreadcrumb('createNews', 'contentManagement');
        } else if (routeName === 'content.news.edit' || (currentUrl.includes('/content/news/') && currentUrl.includes('/edit'))) {
            addContextualBreadcrumb('editNews', 'contentManagement');
        }
        sessionStorage.setItem('previousRoute', routeName);
    } else if (routeName?.startsWith('admin.newsletter')) {
        if (routeName === 'admin.newsletter.index') {
            addContextualBreadcrumb('newsletter', 'contentManagement');
        }
        sessionStorage.setItem('previousRoute', routeName);
    } else if (routeName?.startsWith('admin.email-settings')) {
        if (routeName === 'admin.email-settings.index') {
            addContextualBreadcrumb('emailSettings', 'system');
        }
        sessionStorage.setItem('previousRoute', routeName);
    } else if (routeName?.startsWith('audits')) {
        if (routeName === 'audits.index') {
            addContextualBreadcrumb('audits', 'system');
        } else if (routeName === 'audits.show') {
            addContextualBreadcrumb('auditDetails', 'system');
        }
        sessionStorage.setItem('previousRoute', routeName);
    } else if (routeName?.startsWith('profile.password')) {
        if (routeName === 'profile.password') {
            addContextualBreadcrumb('settings', 'profile');
        }
        sessionStorage.setItem('previousRoute', routeName);
    }

    return breadcrumbs;
});

watch(() => page.url, () => {
    const routeName = route().current();
    if (routeName) {
        sessionStorage.setItem('previousRoute', routeName);
    }
}, { immediate: true });
</script>

<template>
    <BaseDashboardBreadcrumb :breadcrumb-items="breadcrumbItems" />
</template>
